import { Injectable } from '@angular/core';

interface CacheEntry {
    data: unknown;
    timestamp: number;
    ttlMs: number;
}

/**
 * CacheService
 *
 * Generic in-memory cache with per-entry TTL.
 * Used to reduce redundant API calls on public-facing pages.
 *
 * Usage:
 *   const cached = this.cacheService.get<MyType>(key);
 *   if (cached) return of(cached);
 *   return this.http.get<MyType>(url).pipe(tap(data => this.cacheService.set(key, data, ttl)));
 */
@Injectable({
    providedIn: 'root',
})
export class CacheService {
    private readonly cache = new Map<string, CacheEntry>();
    private readonly DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

    /**
     * Get cached data if within TTL, otherwise null.
     */
    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (!entry) return null;

        if (Date.now() - entry.timestamp > entry.ttlMs) {
            this.cache.delete(key);
            return null;
        }

        return entry.data as T;
    }

    /**
     * Store data with a timestamp and optional TTL override.
     */
    set(key: string, data: unknown, ttlMs?: number): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttlMs: ttlMs ?? this.DEFAULT_TTL_MS,
        });
    }

    /**
     * Clear all cache entries, or only those matching a key prefix.
     */
    clear(prefix?: string): void {
        if (!prefix) {
            this.cache.clear();
            return;
        }
        for (const key of this.cache.keys()) {
            if (key.startsWith(prefix)) {
                this.cache.delete(key);
            }
        }
    }

    /**
     * Remove a single cache entry by exact key.
     */
    invalidate(key: string): void {
        this.cache.delete(key);
    }
}
